import {
  EAiActionStatus,
  EAiModelStatus,
  EDatasourceType,
  EDesignerEntityKind,
  ESdChangeReason,
  IActionDefinitionDto,
  IActionFolder,
  IAiModel,
  IAiTemplate,
  ICrewAgentDTO,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import { ColumnSort, OnChangeFn, SortingState } from "@tanstack/react-table";
import { DragUpdate, DropResult } from "react-beautiful-dnd";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { FeatureGates } from "../../../shared/utility/feature-gates";
import { FolderHelper } from "../../../shared/utility/folder-helper";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { NAME_COLLATOR } from "../../../shared/utility/name-collator";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  RECENTLY_MODIFIED_WINDOW_DAYS,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import { SaveStatus } from "../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { DomainAccessModalActionTypes } from "../../../store/domain-access-modal/types";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import { UnsavedChangesModalModuleActionTypes } from "../../../store/unsaved-changes-modal/types";
import { DeleteModal } from "../../common/delete-modal";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge, { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell,
  KuikaListTableUserCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaNumber from "../../ui-kit/kuika-number/kuika-number";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { FolderCreateComponent } from "../left-panel/datadiagramscheme-panel/data-diagram-folder-add";
import "./ai-hub.scss";
import { canCreateAiFolder, describeActionSignature, describeAgentModel } from "./ai-hub-model";
import AiModelDeprecationBanners from "./ai-model-deprecation-banners";
import { canManageActions, removeActionFromItsFolder } from "./datasource-row-actions";

export interface IAiHubProps {
  /** Plan/quota gate — pops the upgrade modal when the domain cannot add another. */
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  /** Defers navigation while another editor holds unsaved changes. */
  setNextCallbackFunction: (cb: () => void) => void;
}

const AI_TYPES: EDatasourceType[] = [EDatasourceType.AIActions, EDatasourceType.AIAgent, EDatasourceType.VisionAction];

/** Droppable id for everything that is in no folder. */
const UNFILED_GROUP = "ai-hub-unfiled";

/**
 * The tab holding every function, beside the "Recently modified" one the page opens on.
 *
 * An action row carries `createdAt` and nothing about later edits, so "recently" cannot be read
 * off the row: when a function was last written, and by whom, comes from the shared
 * entity-modification feed — which is what the recent tab and the last two columns both read.
 */
const ALL_TAB: string = "All Functions";
// Type tabs are derived from what this user can actually reach, never hardcoded:
// AI Agent and Vision Action sit behind an allowlist (FeatureGates), so most
// users can see exactly one type — and a bar reading "All Functions | AI Actions"
// would just be the same list twice.
const TYPE_TABS: { type: EDatasourceType; label: string }[] = [
  { type: EDatasourceType.AIActions, label: "AI Actions" },
  { type: EDatasourceType.AIAgent, label: "AI Agents" },
  { type: EDatasourceType.VisionAction, label: "Vision Actions" }
];

/** One pill in the tab bar. */
interface IAiHubTabOption {
  value: string;
  /**
   * Required, not optional: KuikaTabs renders `icon || label` and never falls back to `value`,
   * so an option without one draws an empty pill.
   */
  label: string;
  dataTestId?: string;
}

type AiFunctionSortKey =
  | "name-asc"
  | "name-desc"
  | "created-desc"
  | "created-asc"
  | "type-asc"
  | "type-desc"
  | "status-asc"
  | "status-desc";

/** The headers and the Sort-by control are one state; this says which column shows a key. */
const SORT_COLUMN_BY_KEY: Record<AiFunctionSortKey, string> = {
  "name-asc": "name",
  "name-desc": "name",
  "created-desc": "created",
  "created-asc": "created",
  "type-asc": "type",
  "type-desc": "type",
  "status-asc": "status",
  "status-desc": "status"
};

/**
 * The table is a shallow TREE, not a flat list: folders come first as collapsible
 * rows and the functions inside them render indented underneath, then everything
 * unfiled follows. Both shapes travel as one row type so a single KuikaListTable
 * renders them — the columns branch on `kind`.
 */
interface IAiFolderRow {
  kind: "folder";
  id: string;
  folder: IActionFolder;
  count: number;
  /**
   * What the folder holds. Own actions come FIRST and sub-folders after, because
   * the table opens a new drop target at every change of group id — a sub-folder in
   * the middle would reopen this folder's group further down, and one id owning two
   * groups means duplicate React keys and a duplicate droppable.
   */
  children: AiHubRow[];
}

interface IAiActionRow {
  kind: "action";
  id: string;
  action: IActionDefinitionDto;
  /** The folder this row renders under — also its droppable group. */
  folderId?: string;
}

type AiHubRow = IAiFolderRow | IAiActionRow;

const SORT_OPTIONS: KuikaSelectboxOption[] = [
  { value: "created-desc", label: "Newest first" },
  { value: "created-asc", label: "Oldest first" },
  { value: "name-asc", label: "Name (A–Z)" },
  { value: "name-desc", label: "Name (Z–A)" },
  { value: "type-asc", label: "Type (A–Z)" },
  { value: "type-desc", label: "Type (Z–A)" },
  // Status sorts by the enum, not the word: Draft (0) before Published (1).
  { value: "status-asc", label: "Draft first" },
  { value: "status-desc", label: "Published first" }
];

export interface IAiHubState {
  actionToDelete: IActionDefinitionDto | null;
  aiModels: IAiModel[];
  aiActionModels: IAiModel[];
  aiTemplates: IAiTemplate[];
  isDeleteActionModalVisible: boolean;
  selectedTypeFilters: EDatasourceType[];
  searchTerm: string;
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  sort: AiFunctionSortKey | null;
  /** Folder ids the user has CLOSED. Folders start open so nothing hides on load. */
  collapsedFolderIds: string[];
  /**
   * Bumped after a drag-drop move. The folder provider edits `project.actionFolders`
   * in place — outside React's knowledge — so this is what tells the panel to re-read
   * it now rather than when the project PATCH comes back.
   */
  moveNonce: number;
  folderToDelete: IActionFolder | null;
  folderModal: { isOpen: boolean; parentFolderId?: string; editing?: IActionFolder };
  isLoading: boolean;
  /** Where the model update's commit stands. Owned by `SaveStatus`, never set directly. */
  updateModelStatus: KuikaButtonStatus;
  /** The row a model update landed on, lit up once the dialog has closed over it. */
  revealRowKey: string | null;
  modelUpdateModal: {
    isOpen: boolean;
    action: IActionDefinitionDto | null;
    type: "shutdown" | "deprecated";
    // AI Action single-model fields:
    currentModelName: string;
    selectedModelId: string | null;
    // AI Agent per-crew-agent fields:
    agentModelUpdates: Array<{
      agentIndex: number; // -1 = manager agent
      agentName: string;
      currentModelId: string;
      currentModelName: string;
      currentStatus: EAiModelStatus;
      selectedModelId: string | null;
    }>;
  };
}

class AiHub extends PureSdComponent<IAiHubProps & IWithSDProps, IAiHubState> {
  private readonly updateModelStatus: SaveStatus = new SaveStatus((updateModelStatus: KuikaButtonStatus): void => {
    this.setState({ updateModelStatus });
  });

  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: IAiHubProps & IWithSDProps) {
    super(props);
    this.state = {
      actionToDelete: null,
      aiModels: [],
      aiActionModels: [],
      aiTemplates: [],
      isDeleteActionModalVisible: false,
      selectedTypeFilters: [],
      searchTerm: "",
      pickedTab: null,
      feed: LOADING_FEED,
      sort: null,
      collapsedFolderIds: [],
      moveNonce: 0,
      folderToDelete: null,
      folderModal: { isOpen: false },
      isLoading: true,
      updateModelStatus: "idle",
      revealRowKey: null,
      modelUpdateModal: {
        isOpen: false,
        action: null,
        type: "shutdown",
        currentModelName: "",
        selectedModelId: null,
        agentModelUpdates: []
      }
    };
  }

  componentDidMount = async () => {
    this.isMounted_ = true;
    // Started beside the model fetch rather than inside its try: the feed never rejects, and a
    // failed feed must not surface as "Failed to load AI models".
    void this.loadFeed();
    try {
      // GetAiModels was called TWICE here — the same endpoint, awaited in parallel,
      // its identical result written to two different state fields. One call.
      const [aiModelResponse, aiTemplatesResponse] = await Promise.all([
        DatasourceService.GetAiModels(),
        DatasourceService.GetAiTemplates()
      ]);
      const aiModels: IAiModel[] = (aiModelResponse.data || []) as unknown as IAiModel[];
      this.setState({
        aiModels,
        aiActionModels: aiModels,
        aiTemplates: aiTemplatesResponse.data || []
      });
    } catch (_error) {
      KMainFunctions.displayWarningNotification("Failed to load AI models");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  /**
   * One create path for all three types. It carries the three guards the left
   * panel applies and the create cards previously skipped:
   *  - the plan/quota check, which pops the upgrade modal instead of opening an
   *    editor the user cannot save,
   *  - a UNIQUE default name — hardcoding "AI Action" made every repeat create
   *    collide with the last one,
   *  - the feature gate for the two internal-preview types.
   */
  private createAiFunction = (type: EDatasourceType, fallbackId: string, namePrefix: string, accessName: string) => {
    if (!FeatureGates.canAccessAiDatasourceType(type)) return;

    const actions: IActionDefinitionDto[] = (this.props.sd.actionDefinitions || []).filter(
      (action: IActionDefinitionDto) =>
        action.actionType !== EDatasourceType.SystemJSFunctions &&
        action.actionType !== EDatasourceType.SystemCSFunctions
    );
    if (KMainFunctions.isDomainHasAccess(accessName, actions) === false) {
      this.props.showDomainAccessRequiredModal(accessName);
      return;
    }

    const datasource: IDatasourceDto =
      this.props.sd.datasources?.find((ds: IDatasourceDto) => ds.datasourceType === type) ??
      ({ id: fallbackId, datasourceType: type } as IDatasourceDto);

    this.props.sd.setEditingActionsDatasource(
      datasource,
      undefined,
      true,
      false,
      undefined,
      KMainFunctions.defaultUniqueListElementNameGenerator(this.props.sd.actionDefinitions, "name", namePrefix),
      false
    );
  };

  createAIAction = () => this.createAiFunction(EDatasourceType.AIActions, "ai", "AI_Action", "Actions");

  createAIAgent = () => this.createAiFunction(EDatasourceType.AIAgent, "ai-agent", "AI_Agent", "AI Agents");

  createVisionAction = () =>
    this.createAiFunction(EDatasourceType.VisionAction, "vision", "Vision_Action", "Vision Action");

  editAIAction = (action: IActionDefinitionDto) => {
    const datasource: IDatasourceDto | undefined = this.props.sd.datasources.find(
      (ds: IDatasourceDto) => ds.id === action.kDatasource_Id
    );
    if (!datasource) return;
    // Opening an editor while another one holds unsaved edits used to discard them
    // silently; defer behind the same guard the left panel uses.
    this.withUnsavedChangesGuard(() => this.props.sd.setEditingActionsDatasource(datasource, action, false, false));
  };

  /** Defers `cb` when any datasource/action/table editor is mid-edit. */
  private withUnsavedChangesGuard = (cb: () => void) => {
    const sd = this.props.sd;
    const isEditorDirty: boolean = !!(
      sd?.activeEditingDatasource ||
      sd?.activeCreatingDatasourceType !== undefined ||
      sd?.editingTable ||
      sd?.editingAction ||
      sd?.activeCreatingTableName ||
      sd?.activeCreatingActionName
    );
    if (isEditorDirty) {
      this.props.setNextCallbackFunction(cb);
      return;
    }
    cb();
  };

  openDeleteActionModal = (action: IActionDefinitionDto) => {
    this.setState({ actionToDelete: action, isDeleteActionModalVisible: true });
  };

  deleteAIAction = async () => {
    if (!this.state.actionToDelete) return;

    try {
      const res = await DatasourceService.DeleteAction(this.state.actionToDelete);

      if (res && res.status === 200) {
        if (this.props.sd.editingAction && this.props.sd.editingAction.id === this.state.actionToDelete.id) {
          this.props.sd.cancelCreatingOrEditingDatasource();
          this.props.sd.setCreateNotificationTemplateActive();
        }

        this.props.sd.loadActionDefinitions();
        this.props.sd.refreshActions();
        KMainFunctions.displaySuccessNotification("Action deleted successfully");
      }
    } catch (error) {
      KMainFunctions.displayErrorNotification(error);
    } finally {
      this.setState({
        isDeleteActionModalVisible: false,
        actionToDelete: null
      });
    }
  };

  /**
   * Takes `props` explicitly, defaulting to the current ones, so `componentDidUpdate` can ask the
   * same question of `prevProps` without a second, drifting copy of this filter.
   */
  getAIItems = (props: IAiHubProps & IWithSDProps = this.props): IActionDefinitionDto[] => {
    return (props.sd?.actionDefinitions || []).filter(
      (a) =>
        [EDatasourceType.AIActions, EDatasourceType.AIAgent, EDatasourceType.VisionAction].includes(a.actionType) &&
        FeatureGates.canAccessAiDatasourceType(a.actionType)
    );
  };

  /**
   * The page stays mounted through a create or a duplicate started elsewhere, so
   * `actionDefinitions` can gain or lose a row without this page ever remounting to re-fetch the
   * feed on its own. The id set is the smallest correct signal: it fires on a
   * create/duplicate/delete and stays quiet on an in-place rename, which this page reads off the
   * row itself rather than off the feed.
   */
  componentDidUpdate = (prevProps: Readonly<IAiHubProps & IWithSDProps>): void => {
    if (haveItemIdsChanged(this.getAIItems(prevProps), this.getAIItems(), (row: IActionDefinitionDto) => row.id)) {
      void this.loadFeed();
    }
  };

  getCreatedTimestamp = (item: IActionDefinitionDto): number => {
    // CreatedAt is non-nullable server-side but old rows can carry the CLR default,
    // which parses to year 1 — treat anything unparseable as "no timestamp" so it
    // sorts last. Recency is not read from here: it comes from the modification feed.
    const parsed: number = item.createdAt ? new Date(item.createdAt).getTime() : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  };

  /**
   * The feed answers for every action in the workspace, not only the AI ones; the page keeps the
   * rows it lists by intersecting on id, and the surplus falls away.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed([EDesignerEntityKind.Action], projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the full list when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every function, not the searched or type-filtered ones: a query that matches
      // nothing recent must not move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<IActionDefinitionDto>(
        this.getAIItems(),
        this.state.feed.index,
        (row: IActionDefinitionDto) => row.id,
        Date.now()
      ),
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: ALL_TAB
    });

  /**
   * The recent tab's functions, newest change first. They still pass through `buildRows`, so a
   * function filed in a folder stays under it and the feed's order holds within each group.
   */
  getRecentAIItems = (): IActionDefinitionDto[] =>
    selectRecentlyModified<IActionDefinitionDto>({
      items: this.getFilteredAIItems(),
      index: this.state.feed.index,
      getId: (row: IActionDefinitionDto) => row.id,
      getName: (row: IActionDefinitionDto) => row.name ?? "",
      now: Date.now()
    });

  getFilteredAIItems = (): IActionDefinitionDto[] => {
    let allItems = this.getAIItems();

    // Filter by search term
    if (this.state.searchTerm) {
      const normalizedSearch = this.normalizeSearchValue(this.state.searchTerm);
      allItems = allItems.filter((item) => this.normalizeSearchValue(item.name).includes(normalizedSearch));
    }

    // Filter by selected types
    if (this.state.selectedTypeFilters.length > 0) {
      allItems = allItems.filter((item) => this.state.selectedTypeFilters.includes(item.actionType));
    }

    const activeTab: string = this.getActiveTab();
    const tabType: EDatasourceType | undefined = TYPE_TABS.find(
      (t: { type: EDatasourceType; label: string }) => t.label === activeTab
    )?.type;
    if (tabType !== undefined) {
      allItems = allItems.filter((item) => item.actionType === tabType);
    }

    // The fetch arrives in insertion order; a "your functions" table reading
    // oldest-first is the wrong default. An explicit Sort-by choice overrides it.
    const effectiveSort: AiFunctionSortKey = this.state.sort ?? "created-desc";
    // The comparators the antd table had before this list moved to KuikaListTable.
    const compareAscending = (a: IActionDefinitionDto, b: IActionDefinitionDto): number => {
      if (effectiveSort.startsWith("name")) return NAME_COLLATOR.compare(a.name, b.name);
      if (effectiveSort.startsWith("type")) {
        return this.getTypeLabel(a.actionType).localeCompare(this.getTypeLabel(b.actionType));
      }
      if (effectiveSort.startsWith("status")) {
        return (a.aiActionStatus ?? EAiActionStatus.Published) - (b.aiActionStatus ?? EAiActionStatus.Published);
      }
      return this.getCreatedTimestamp(a) - this.getCreatedTimestamp(b);
    };

    /**
     * Ties are the common case on this list, not the exception: every row can carry the same
     * Type, and every row can be Published. `sort` is stable, so a tie left the rows in the
     * order the fetch happened to return — which is not the order the reader was looking at a
     * moment ago, and reads as the list shuffling itself. The list's own resting order settles
     * it instead: newest first, then the name.
     */
    const compareTie = (a: IActionDefinitionDto, b: IActionDefinitionDto): number => {
      const byCreated: number = this.getCreatedTimestamp(b) - this.getCreatedTimestamp(a);
      return byCreated !== 0 ? byCreated : NAME_COLLATOR.compare(a.name, b.name);
    };

    const isDescending: boolean = effectiveSort.endsWith("-desc");
    return [...allItems].sort((a: IActionDefinitionDto, b: IActionDefinitionDto): number => {
      const primary: number = compareAscending(a, b);
      if (primary !== 0) return isDescending ? -primary : primary;
      // Deliberately outside the flip: descending reverses the column that was asked for,
      // not the order the equals settle into.
      return compareTie(a, b);
    });
  };

  /** The Sort-by choice as tanstack states it, so the matching header carries the arrow. */
  getSortingState = (): SortingState => {
    const sort: AiFunctionSortKey | null = this.state.sort;
    if (!sort) return [];
    return [{ id: SORT_COLUMN_BY_KEY[sort], desc: sort.endsWith("-desc") }];
  };

  /**
   * A header click writes the state the Sort-by control writes; `buildRows` still owns the
   * order. That is what keeps folders on top in BOTH directions — a comparator could not,
   * since a descending sort flips its result and the folders would sink.
   */
  handleSortingChange: OnChangeFn<SortingState> = (updater): void => {
    const next: SortingState = typeof updater === "function" ? updater(this.getSortingState()) : updater;
    const entry: ColumnSort | undefined = next[0];
    // Third click clears the sort; the list falls back to newest.
    if (!entry) {
      this.setState({ sort: null });
      return;
    }
    this.setState({ sort: `${entry.id}-${entry.desc ? "desc" : "asc"}` as AiFunctionSortKey });
  };

  /** Folders that belong to the AI datasources, top level first. */
  getAiFolders = (): IActionFolder[] => {
    const aiDatasourceIds: string[] = (this.props.sd.datasources || [])
      .filter((ds: IDatasourceDto) => AI_TYPES.includes(ds.datasourceType))
      .map((ds: IDatasourceDto) => ds.id);
    return (this.props.sd.project?.actionFolders || []).filter(
      (folder: IActionFolder) => !folder.parentFolderId && aiDatasourceIds.includes(folder.dataSourceType)
    );
  };

  isFolderOpen = (folderId: string): boolean => !this.state.collapsedFolderIds.includes(folderId);

  toggleFolder = (folderId: string) => {
    this.setState((prev: IAiHubState) => ({
      collapsedFolderIds: prev.collapsedFolderIds.includes(folderId)
        ? prev.collapsedFolderIds.filter((id: string) => id !== folderId)
        : [...prev.collapsedFolderIds, folderId]
    }));
  };

  /**
   * The table asks, this answers. Keeping the policy here is what lets folders
   * default to OPEN: the state stores the CLOSED ones, so a folder created later is
   * open without anyone having to remember to seed it. Only folders expand — a
   * function row is a leaf.
   */
  isRowExpanded = (row: AiHubRow): boolean => row.kind === "folder" && this.isFolderOpen(row.id);

  /**
   * Builds the folder TREE the table renders: folders in name order, each holding its
   * own actions and then its sub-folders, with everything unfiled following at the
   * root. The table flattens it, so nothing here knows or cares what is open.
   *
   * A folder is kept when it still has a matching child even if its own name does not
   * match the search, so filtering never hides the only path to a result.
   *
   * Not memoised: the folder provider mutates `project.actionFolders` in place for
   * sub-folder writes, so neither the array nor the project object changes identity —
   * any cheap cache key would go stale and swallow a newly created sub-folder. The
   * build is O(functions) over a list that is tens of rows long.
   */
  buildRows = (items: IActionDefinitionDto[]): AiHubRow[] => {
    const byId: Map<string, IActionDefinitionDto> = new Map(items.map((item: IActionDefinitionDto) => [item.id, item]));
    const claimed: Set<string> = new Set();
    const rows: AiHubRow[] = [];
    // An empty folder is a real place: you just created it, and it is a drop target.
    // It disappears only while a filter is on, where "empty" no longer means "new"
    // but "everything inside was filtered out" — a row that answers nothing.
    const hideEmptyFolders: boolean = this.isFiltering();

    const childrenOf = (folder: IActionFolder): IActionDefinitionDto[] => {
      const ids: string[] = folder.actionIds || [];
      const found: IActionDefinitionDto[] = [];
      ids.forEach((id: string) => {
        const item: IActionDefinitionDto | undefined = byId.get(id);
        if (item) {
          found.push(item);
          claimed.add(id);
        }
      });
      return found;
    };

    // Folders stay pinned above the functions whichever way the sort runs — that is the
    // point of sorting in here rather than in the table. Within the group they are ordered
    // by name, and a name sort is the one choice that means something for a folder row, so
    // Z–A turns them around too; Type/Status/Created say nothing about a folder and leave
    // the group alphabetical.
    const isFolderOrderReversed: boolean = this.state.sort === "name-desc";
    const byName = (a: IActionFolder, b: IActionFolder): number =>
      isFolderOrderReversed ? NAME_COLLATOR.compare(b.name, a.name) : NAME_COLLATOR.compare(a.name, b.name);
    const toActionRow = (item: IActionDefinitionDto, folderId?: string): IAiActionRow => ({
      kind: "action",
      id: item.id,
      action: item,
      folderId
    });

    this.getAiFolders()
      .slice()
      .sort(byName)
      .forEach((folder: IActionFolder) => {
        const own: IActionDefinitionDto[] = childrenOf(folder);
        const subRows: AiHubRow[] = [];
        let nested: number = 0;
        (folder.children || [])
          .slice()
          .sort(byName)
          .forEach((sub: IActionFolder) => {
            const subItems: IActionDefinitionDto[] = childrenOf(sub);
            if (subItems.length === 0 && hideEmptyFolders) return;
            nested += subItems.length;
            subRows.push({
              kind: "folder",
              id: sub.id,
              folder: sub,
              count: subItems.length,
              children: subItems.map((item: IActionDefinitionDto) => toActionRow(item, sub.id))
            });
          });

        const total: number = own.length + nested;
        if (total === 0 && hideEmptyFolders) return;
        rows.push({
          kind: "folder",
          id: folder.id,
          folder,
          count: total,
          // Own actions before sub-folders — see the note on IAiFolderRow.children.
          children: [...own.map((item: IActionDefinitionDto) => toActionRow(item, folder.id)), ...subRows]
        });
      });

    items
      .filter((item: IActionDefinitionDto) => !claimed.has(item.id))
      .forEach((item: IActionDefinitionDto) => rows.push(toActionRow(item)));

    return rows;
  };

  /** True while search, the type filter or a tab is narrowing what the table shows. */
  isFiltering = (): boolean =>
    Boolean(this.state.searchTerm) || this.state.selectedTypeFilters.length > 0 || this.getActiveTab() !== ALL_TAB;

  handleTypeFilterChange = (selectedTypes: EDatasourceType[]) => {
    this.setState({ selectedTypeFilters: selectedTypes });
  };

  handleSearchChange = (searchTerm: string) => {
    this.setState({ searchTerm });
  };

  private normalizeSearchValue = (value?: string): string => {
    if (!value) return "";

    return KMainFunctions.turkishCharsToEnglish(value)
      .toLowerCase()
      .replace(/[\s_-]+/g, "")
      .trim();
  };

  private formatModelName = (name?: string): string => {
    if (!name) return "Unknown Model";

    const normalized = name.replace(/[-_]+/g, " ").trim();

    const tokens = normalized.split(/\s+/).map((token) => {
      if (/^[0-9.]+$/.test(token)) return token;

      if (/^[a-zA-Z]{1,3}$/.test(token)) return token.toUpperCase();

      const match = token.match(/^([a-zA-Z]+)(.*)$/);
      if (match) {
        const alpha = match[1];
        const rest = match[2];
        if (alpha.length <= 3) {
          return alpha.toUpperCase() + rest;
        }

        return alpha[0].toUpperCase() + alpha.slice(1).toLowerCase() + rest;
      }

      return token[0].toUpperCase() + token.slice(1).toLowerCase();
    });

    return tokens.join(" ");
  };

  getActionModelDeprecationStatus = (record: IActionDefinitionDto): EAiModelStatus | null => {
    if (record.actionType !== EDatasourceType.AIActions && record.actionType !== EDatasourceType.AIAgent) {
      return null;
    }

    if (record.actionType === EDatasourceType.AIActions) {
      const aiInfo = this.props.sd.getStringifiedAiActionInfo(record.aiActionInfo);
      if (!aiInfo) return null;
      // Skip template-only actions (no direct model set)
      if (!aiInfo.model && aiInfo.templateId) return null;
      if (!aiInfo.model) return null;
      const rawModel = aiInfo.model;
      const matched = this.state.aiActionModels.find((m) => m.id === rawModel || m.name === rawModel);
      if (!matched || matched.status == null) return null;
      return matched.status as EAiModelStatus;
    }

    if (record.actionType === EDatasourceType.AIAgent) {
      const agentInfo = this.props.sd.getStringifiedAiAgentInfo(record.aiAgentInfo) as ICrewAgentDTO | null;
      if (!agentInfo) return null;

      const modelRefs: (string | undefined)[] = [];

      agentInfo.crewAgents?.forEach((agent) => {
        if (!agent.model) return;
        if (typeof agent.model === "string") {
          modelRefs.push(agent.model);
        } else {
          const modelObj = agent.model as { id?: string; name?: string };
          modelRefs.push(modelObj.id || modelObj.name);
        }
      });

      if (agentInfo.managerAgent?.model) {
        modelRefs.push(agentInfo.managerAgent.model);
      }

      if (modelRefs.length === 0) return null;

      let worstStatus: EAiModelStatus = EAiModelStatus.Active;
      for (const ref of modelRefs) {
        if (!ref) continue;
        const matched = this.state.aiModels.find((m) => m.id === ref || m.name === ref);
        if (!matched || matched.status == null) continue;
        const s = matched.status as EAiModelStatus;
        if (s > worstStatus) worstStatus = s;
      }
      return worstStatus === EAiModelStatus.Active ? null : worstStatus;
    }

    return null;
  };

  getDeprecationWarningIcon = (record: IActionDefinitionDto) => {
    const status = this.getActionModelDeprecationStatus(record);
    if (status === EAiModelStatus.Deprecated) {
      return (
        <KuikaTooltip content="This action uses a deprecated model" position="top">
          <span className="ai-hub__deprecation-icon">
            <KuikaSvg type="common" name="alert-triangle" width={16} height={16} color="var(--yellow-6)" />
          </span>
        </KuikaTooltip>
      );
    }
    if (status === EAiModelStatus.Shutdown) {
      return (
        <KuikaTooltip content="This action uses a shutdown model" position="top">
          <span className="ai-hub__deprecation-icon">
            <KuikaSvg type="common" name="alert-triangle" width={16} height={16} color="var(--red-6)" />
          </span>
        </KuikaTooltip>
      );
    }
    return null;
  };

  getTypeOptions = () =>
    [
      { label: "AI Action", value: EDatasourceType.AIActions },
      { label: "AI Agent", value: EDatasourceType.AIAgent },
      { label: "Vision Action", value: EDatasourceType.VisionAction }
    ].filter((o) => FeatureGates.canAccessAiDatasourceType(o.value));

  getTypeLabel = (type: EDatasourceType): string => {
    switch (type) {
      case EDatasourceType.AIActions:
        return "AI Action";
      case EDatasourceType.AIAgent:
        return "AI Agent";
      case EDatasourceType.VisionAction:
        return "Vision Action";
      default:
        return "";
    }
  };

  getStatusBadge = (record: IActionDefinitionDto) => {
    const status = record.aiActionStatus ?? EAiActionStatus.Published;
    if (status === EAiActionStatus.Draft) {
      return <KuikaBadge content="Draft" color="yellow" />;
    }
    return <KuikaBadge content="Published" color="green" />;
  };

  /**
   * The create cards. Their headings say what clicking one DOES — the wording every other
   * tool's landing uses ("Create a blank email", "Create a blank job"). AI Functions drops
   * the "blank": these three create different KINDS of function, not blank copies of one.
   */
  getCreateOptions = () =>
    [
      {
        type: EDatasourceType.AIActions,
        heading: "Create an AI Action",
        description: "Performs a specific task using AI capabilities.",
        iconName: "ai-action",
        bgColor: "var(--purple-1)",
        iconColor: "var(--purple-5)",
        onClick: this.createAIAction
      },
      {
        type: EDatasourceType.AIAgent,
        heading: "Create an AI Agent",
        description: "Acts autonomously using specialised reasoning and tool usage.",
        iconName: "ai-agent",
        bgColor: "var(--blue-1)",
        iconColor: "var(--accent-fg)",
        onClick: this.createAIAgent
      },
      {
        type: EDatasourceType.VisionAction,
        heading: "Create a Vision Action",
        description: "Processes and interprets visual data with computer-vision models.",
        iconName: "vision-action",
        bgColor: "var(--green-1)",
        iconColor: "var(--green-5)",
        onClick: this.createVisionAction
      }
    ].filter((o) => FeatureGates.canAccessAiDatasourceType(o.type));

  getIconForType = (type: EDatasourceType) => {
    switch (type) {
      case EDatasourceType.AIActions:
        return {
          name: "ai-action",
          color: "var(--purple-5)",
          background: "var(--purple-1)"
        };
      case EDatasourceType.AIAgent:
        return {
          name: "ai-agent",
          color: "var(--accent-fg)",
          background: "var(--blue-1)"
        };
      case EDatasourceType.VisionAction:
        return {
          name: "vision-action",
          color: "var(--green-5)",
          background: "var(--green-1)"
        };
      default:
        return {
          name: "sparkles",
          color: "var(--purple-5)",
          background: "var(--purple-1)"
        };
    }
  };

  getDeprecatedModelsUsage = () => {
    const items = this.getAIItems();
    return items.filter((item) => this.getActionModelDeprecationStatus(item) === EAiModelStatus.Deprecated);
  };

  getShutdownModelsUsage = () => {
    const items = this.getAIItems();
    return items.filter((item) => this.getActionModelDeprecationStatus(item) === EAiModelStatus.Shutdown);
  };

  resolveModelName = (modelId: string, models: IAiModel[]): string => {
    const matched = models.find((m) => m.id === modelId || m.name === modelId);
    return this.formatModelName(matched?.name || modelId);
  };

  getActionCurrentModelName = (action: IActionDefinitionDto): string => {
    if (action.actionType === EDatasourceType.AIActions) {
      const aiInfo = this.props.sd.getStringifiedAiActionInfo(action.aiActionInfo);
      if (!aiInfo?.model) return "Unknown Model";
      return this.resolveModelName(aiInfo.model, this.state.aiActionModels);
    }
    return "Unknown Model";
  };

  /**
   * The Model column's label. An AI Action resolves the same way the deprecation banner already
   * does (`getActionCurrentModelName`); an AI Agent's manager + crew models are collected and
   * resolved by the pure `describeAgentModel` (`ai-hub-model.ts`) so that logic is testable
   * without a live `sd`. Vision Actions carry no model reference this page tracks, so they read
   * as an em dash.
   */
  getModelBadgeLabel = (action: IActionDefinitionDto): string => {
    if (action.actionType === EDatasourceType.AIActions) {
      return this.getActionCurrentModelName(action);
    }
    if (action.actionType === EDatasourceType.AIAgent) {
      const agentInfo = this.props.sd.getStringifiedAiAgentInfo(action.aiAgentInfo) as ICrewAgentDTO | null;
      return describeAgentModel(agentInfo, (modelId: string): string =>
        this.resolveModelName(modelId, this.state.aiModels)
      );
    }
    return "—";
  };

  getActiveModelOptions = (forActionType: EDatasourceType) => {
    const models = forActionType === EDatasourceType.AIAgent ? this.state.aiModels : this.state.aiActionModels;
    return models
      .filter((m) => m.status === EAiModelStatus.Active || m.status == null)
      .map((m) => ({
        value: m.id || m.name,
        label: this.formatModelName(m.name)
      }));
  };

  buildAgentModelUpdates = (action: IActionDefinitionDto, type: "shutdown" | "deprecated") => {
    const agentInfo = this.props.sd.getStringifiedAiAgentInfo(action.aiAgentInfo) as ICrewAgentDTO | null;
    if (!agentInfo) return [];

    const targetStatus = type === "shutdown" ? EAiModelStatus.Shutdown : EAiModelStatus.Deprecated;
    const updates: IAiHubState["modelUpdateModal"]["agentModelUpdates"] = [];

    agentInfo.crewAgents?.forEach((agent, index) => {
      if (!agent.model) return;
      const modelObj = agent.model as { id?: string; name?: string };
      const modelId = typeof agent.model === "string" ? agent.model : modelObj.id || modelObj.name;
      if (!modelId) return;
      const matched = this.state.aiModels.find((m) => m.id === modelId || m.name === modelId);
      const status = matched?.status as EAiModelStatus;
      if (status !== EAiModelStatus.Deprecated && status !== EAiModelStatus.Shutdown) return;
      if (type === "deprecated" && status === EAiModelStatus.Shutdown) return; // only show relevant
      updates.push({
        agentIndex: index,
        agentName: agent.name || `Agent ${index + 1}`,
        currentModelId: modelId,
        currentModelName: this.resolveModelName(modelId, this.state.aiModels),
        currentStatus: status,
        selectedModelId: null
      });
    });

    if (agentInfo.managerAgent?.model) {
      const modelId = agentInfo.managerAgent.model;
      const matched = this.state.aiModels.find((m) => m.id === modelId || m.name === modelId);
      const status = matched?.status as EAiModelStatus;
      if (
        (status === EAiModelStatus.Deprecated || status === EAiModelStatus.Shutdown) &&
        !(type === "deprecated" && status === EAiModelStatus.Shutdown)
      ) {
        updates.push({
          agentIndex: -1,
          agentName: "Manager Agent",
          currentModelId: modelId,
          currentModelName: this.resolveModelName(modelId, this.state.aiModels),
          currentStatus: status,
          selectedModelId: null
        });
      }
    }

    return updates;
  };

  openModelUpdateModal = (action: IActionDefinitionDto, type: "shutdown" | "deprecated") => {
    const agentModelUpdates =
      action.actionType === EDatasourceType.AIAgent ? this.buildAgentModelUpdates(action, type) : [];
    this.setState({
      modelUpdateModal: {
        isOpen: true,
        action,
        type,
        currentModelName: this.getActionCurrentModelName(action),
        selectedModelId: null,
        agentModelUpdates
      }
    });
  };

  handleRowRevealed = (): void => {
    this.setState({ revealRowKey: null });
  };

  closeModelUpdateModal = () => {
    this.setState({
      modelUpdateModal: {
        isOpen: false,
        action: null,
        type: "shutdown",
        currentModelName: "",
        selectedModelId: null,
        agentModelUpdates: []
      }
    });
  };

  handleUpdateModel = async () => {
    const { action, selectedModelId, agentModelUpdates } = this.state.modelUpdateModal;
    if (!action) return;
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-send the update behind the closing dialog.
    if (!this.updateModelStatus.canStart) return;

    this.updateModelStatus.start();
    try {
      if (action.actionType === EDatasourceType.AIActions) {
        if (!selectedModelId) {
          this.updateModelStatus.reset();
          return;
        }
        const aiInfo = this.props.sd.getStringifiedAiActionInfo(action.aiActionInfo);
        if (aiInfo) {
          aiInfo.model = selectedModelId;
          action.aiActionInfo = aiInfo;
        }
      } else if (action.actionType === EDatasourceType.AIAgent) {
        if (!action.aiAgentInfo) {
          this.updateModelStatus.reset();
          return;
        }
        const agentInfo = JSON.parse(action.aiAgentInfo) as ICrewAgentDTO;
        for (const update of agentModelUpdates) {
          if (!update.selectedModelId) continue;
          if (update.agentIndex === -1) {
            if (agentInfo.managerAgent) {
              (agentInfo.managerAgent as any).model = { id: update.selectedModelId };
            }
          } else {
            const agent = agentInfo.crewAgents?.[update.agentIndex];
            if (agent) {
              (agent as any).model = { id: update.selectedModelId };
            }
          }
        }
        action.aiAgentInfo = JSON.stringify(agentInfo);
      }

      const res = await DatasourceService.SaveAction(action);
      if (!res || res.status !== 200) {
        // The dialog stays open on a failure now, so the button is where it is said.
        this.updateModelStatus.fail();
        return;
      }
      this.props.sd.loadActionDefinitions();
      this.props.sd.refreshActions();
      // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`. The row it
      // changed then lights up in the list behind; the button has already said "Updated".
      const updatedActionId: string = action.id;
      this.updateModelStatus.succeed((): void => {
        this.closeModelUpdateModal();
        this.setState({ revealRowKey: updatedActionId });
      });
    } catch (error) {
      // Kept open with "Try again" on the button; the toast says why, which the button cannot.
      this.updateModelStatus.fail();
      KMainFunctions.displayErrorNotification(error);
    }
  };

  handleFixNow = () => {
    const shutdownItems = this.getShutdownModelsUsage();
    if (shutdownItems.length > 0) {
      this.editAIAction(shutdownItems[0]);
    }
  };

  handleReviewAll = () => {
    const deprecatedItems = this.getDeprecatedModelsUsage();
    if (deprecatedItems.length > 0) {
      this.editAIAction(deprecatedItems[0]);
    }
  };

  isTemplateBasedAction = (record: IActionDefinitionDto): boolean =>
    record.actionType === EDatasourceType.AIActions &&
    !!this.props.sd.getStringifiedAiActionInfo(record.aiActionInfo)?.templateId;

  /** hierarchical vs sequential, read off the agent's own config. */
  getAgentProcessType = (record: IActionDefinitionDto): "hierarchical" | "sequential" | null => {
    if (record.actionType !== EDatasourceType.AIAgent) return null;
    const info = this.props.sd.getStringifiedAiAgentInfo(record.aiAgentInfo);
    const raw: unknown = info?.processType;
    if (raw === undefined || raw === null) return null;
    return Number(raw) === 1 ? "hierarchical" : "sequential";
  };

  getTableColumns = (): KuikaListTableColumn<AiHubRow>[] => [
    {
      id: "name",
      header: () => "Name",
      // The accessors only make the headers sortable — tanstack ignores a column without
      // one. `buildRows` still decides the order.
      accessorFn: (row: AiHubRow): string => (row.kind === "folder" ? row.folder.name : row.action.name),
      cell: (row: AiHubRow) => {
        if (row.kind === "folder") {
          const isOpen: boolean = this.isFolderOpen(row.folder.id);
          return (
            <span className="ai-hub__row">
              <button
                type="button"
                className="ai-hub__folder-toggle"
                aria-expanded={isOpen}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  this.toggleFolder(row.folder.id);
                }}
              >
                <KuikaSvg type="arrows" name={isOpen ? "chevron-down" : "chevron-right"} width={14} height={14} />
              </button>
              <span className="ai-hub__folder-icon">
                <KuikaSvg name="folder" type="sidebar-icons" width={14} height={14} />
              </span>
              <KuikaTextMedium className="ai-hub__folder-name">{row.folder.name}</KuikaTextMedium>
              <KuikaSmallText className="ai-hub__folder-count">
                {/* Rows drag between folders, so the count changes under the reader's eye. */}
                <KuikaNumber value={row.count} />
              </KuikaSmallText>
            </span>
          );
        }
        const icon = this.getIconForType(row.action.actionType);
        return (
          <span
            className="ai-hub__row ai-hub__name-cell"
            // Per-type retint of the shared name tile — tokens, resolved by
            // getIconForType, so both themes stay correct.
            style={
              {
                "--kuika-list-table-icon-bg": icon.background,
                "--kuika-list-table-icon-color": icon.color
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell
              name={row.action.name}
              icon={<KuikaSvg type="datasource-icons" name={icon.name} width={12} height={12} />}
              secondary={describeActionSignature(row.action)}
            />
          </span>
        );
      }
    },
    {
      id: "type",
      header: () => "Type",
      accessorFn: (row: AiHubRow): string => (row.kind === "folder" ? "" : this.getTypeLabel(row.action.actionType)),
      width: 180,
      cell: (row: AiHubRow) => {
        if (row.kind === "folder") return null;
        const processType = this.getAgentProcessType(row.action);
        return (
          <span className="ai-hub__type-cell">
            {this.getTypeLabel(row.action.actionType)}
            {processType && (
              <KuikaBadge
                type="label"
                size="small"
                color={processType === "hierarchical" ? "orange" : "blue"}
                content={processType}
              />
            )}
          </span>
        );
      }
    },
    {
      id: "status",
      header: () => "Status",
      accessorFn: (row: AiHubRow): number =>
        row.kind === "folder" ? -1 : (row.action.aiActionStatus ?? EAiActionStatus.Published),
      // tanstack descends first on numbers; the old table ascended first everywhere.
      sortDescFirst: false,
      width: 120,
      cell: (row: AiHubRow) => {
        if (row.kind === "folder") return null;
        const isDraft: boolean = (row.action.aiActionStatus ?? EAiActionStatus.Published) === EAiActionStatus.Draft;
        return (
          <KuikaListTableStatusCell label={isDraft ? "Draft" : "Published"} color={isDraft ? "yellow" : "green"} />
        );
      }
    },
    {
      id: "model",
      header: () => "Model",
      width: 160,
      // Not one of `AiFunctionSortKey` — the Sort-by control and the headers share that closed
      // union, and a header outside it would write a key the control cannot represent.
      enableSorting: false,
      cell: (row: AiHubRow) => {
        if (row.kind === "folder") return null;
        return (
          <KuikaBadge
            type="label"
            size="small"
            variant="subtle"
            color="grey"
            content={this.getModelBadgeLabel(row.action)}
          />
        );
      }
    },
    {
      id: "created",
      // The cell's text is the date and the header sorts on it; the avatar beside it is the
      // "by whom". Saying "Created By" promised a sort on the person that has never existed.
      header: () => "Created",
      accessorFn: (row: AiHubRow): number => (row.kind === "folder" ? 0 : this.getCreatedTimestamp(row.action)),
      sortDescFirst: false,
      width: 220,
      cell: (row: AiHubRow) => {
        if (row.kind === "folder") return null;
        const firstName: string = row.action.createdUser?.firstName ?? "";
        const lastName: string = row.action.createdUser?.lastName ?? "";
        const fullName: string = [firstName, lastName].filter(Boolean).join(" ");
        const initials: string = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
        return (
          <span className="ai-hub__created-cell">
            <KuikaListTableUserCell
              caption={row.action.createdAt ? KMainFunctions.formatRelativeTime(row.action.createdAt) : undefined}
              name={fullName || undefined}
              photoUrl={row.action.createdUser?.profilePhotoUrl || undefined}
              initials={initials || undefined}
            />
            {this.getDeprecationWarningIcon(row.action)}
          </span>
        );
      }
    },
    this.getModificationColumn()
  ];

  /**
   * The shared Last modified column, with its header unsortable here. The Sort-by control and
   * the headers are one state on this page, keyed by a closed union of sort keys — a header
   * outside that union would write a key the control cannot show and the list would quietly
   * sort by something else. Sorting by when a function was last touched is what the "Recently
   * modified" tab is.
   *
   * Typed to the tree's row rather than to the action: a folder is not an entity the feed dates,
   * so it answers no id and its cell reads as an em dash.
   */
  getModificationColumn = (): KuikaListTableColumn<AiHubRow> => ({
    ...buildLastModifiedColumn<AiHubRow>(this.state.feed.index, (row: AiHubRow): string | undefined =>
      row.kind === "action" ? row.action.id : undefined
    ),
    enableSorting: false
  });

  /** True when the user may create/edit/delete AI functions in this project. */
  canManage = (): boolean => canManageActions(this.props.sd);

  getRowActions = (): KuikaListTableRowAction<AiHubRow>[] => {
    if (!this.canManage()) return [];
    return [
      // ---- folder rows ---------------------------------------------------------
      {
        key: "folder-rename",
        label: "Rename",
        icon: <KuikaSvg name="edit" width={14} height={14} color="var(--dark-7)" />,
        hidden: (row: AiHubRow): boolean => row.kind !== "folder",
        onSelect: (row: AiHubRow) => {
          if (row.kind !== "folder") return;
          this.setState({ folderModal: { isOpen: true, editing: row.folder } });
        }
      },
      {
        key: "folder-new-sub",
        label: "New sub folder",
        icon: <KuikaSvg name="folder" type="sidebar-icons" width={14} height={14} color="var(--dark-7)" />,
        // One level of nesting, matching what the folder provider supports.
        hidden: (row: AiHubRow): boolean => row.kind !== "folder" || !!row.folder.parentFolderId,
        onSelect: (row: AiHubRow) => {
          if (row.kind !== "folder") return;
          this.setState({ folderModal: { isOpen: true, parentFolderId: row.folder.id } });
        }
      },
      {
        key: "folder-delete",
        label: "Delete",
        danger: true,
        icon: <KuikaSvg name="trash" width={14} height={14} />,
        hidden: (row: AiHubRow): boolean => row.kind !== "folder",
        onSelect: (row: AiHubRow) => {
          if (row.kind !== "folder") return;
          this.setState({ folderToDelete: row.folder });
        }
      },
      // ---- action rows ---------------------------------------------------------
      {
        key: "edit",
        label: "Edit",
        icon: <KuikaSvg name="edit" width={14} height={14} color="var(--dark-7)" />,
        // A template-based AI Action is owned by its template; editing happens there.
        hidden: (row: AiHubRow): boolean => row.kind !== "action" || this.isTemplateBasedAction(row.action),
        onSelect: (row: AiHubRow) => {
          if (row.kind !== "action") return;
          this.editAIAction(row.action);
        }
      },
      {
        key: "unfile",
        label: "Move out of folder",
        icon: <KuikaSvg name="folder-off" type="sidebar-icons" width={14} height={14} color="var(--dark-7)" />,
        // Dragging can only reach a group that is on screen, and the unfiled group is
        // absent when nothing is unfiled — so with every function filed there was no
        // way back out at all. This is that way out; it is offered only from inside a
        // folder, where it means something.
        hidden: (row: AiHubRow): boolean => row.kind !== "action" || !row.folderId || !this.canManage(),
        onSelect: (row: AiHubRow) => {
          if (row.kind !== "action") return;
          this.moveActionToFolder(row.action, null);
        }
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        icon: <KuikaSvg name="trash" width={14} height={14} />,
        hidden: (row: AiHubRow): boolean => row.kind !== "action",
        onSelect: (row: AiHubRow) => {
          if (row.kind !== "action") return;
          this.openDeleteActionModal(row.action);
        }
      }
    ];
  };

  /** Where a new folder is created: the datasource that owns AI Actions. */
  getFolderDatasourceId = (): string =>
    this.props.sd.datasources?.find((ds: IDatasourceDto) => ds.datasourceType === EDatasourceType.AIActions)?.id ?? "";

  /**
   * A folder needs two things that a fresh project has neither of: something to file, and the AI
   * Actions datasource it hangs off. Without them "New folder" was a button that could only fail,
   * so it is not offered at all. The count is the project's, not the list's — gating on the
   * filtered rows would make the button vanish as you typed in the search box.
   */
  canCreateFolder = (): boolean =>
    canCreateAiFolder(this.canManage(), this.getAIItems().length, this.getFolderDatasourceId());

  /**
   * Folders hang off the AI Actions datasource, so a project without one has
   * nowhere to put them — `FolderCreateComponent` would drop the submit on the
   * floor with no feedback. Say so instead of opening a modal that cannot save.
   * `canCreateFolder` normally keeps the button away; this still answers if the
   * datasource goes while the page is open.
   */
  openNewFolderModal = () => {
    if (!this.getFolderDatasourceId()) {
      KMainFunctions.displayWarningNotification("Create an AI function first — folders live under its datasource.");
      return;
    }
    this.setState({ folderModal: { isOpen: true } });
  };

  closeFolderModal = () => this.setState({ folderModal: { isOpen: false } });

  deleteFolder = () => {
    const folder: IActionFolder | null = this.state.folderToDelete;
    if (!folder) return;
    if (folder.parentFolderId) {
      this.props.sd.deleteActionFolderInFolder(folder.parentFolderId, folder.id);
    } else {
      this.props.sd.deleteActionFolder(folder.id);
    }
    this.setState({ folderToDelete: null });
  };

  /** Moves an action between folders (or out of every folder). */
  /**
   * A move is ONE project write, and the panel repaints without waiting for it.
   *
   * Two separate costs used to stack up here. Every provider call PATCHes the project
   * and only re-renders once the request returns, and a folder→folder move made two of
   * them (remove, then add) — so the row sat unfiled at depth 0 for a whole round-trip
   * before the second write indented it. The explicit remove was redundant either way:
   * both add paths already evict the action from every other folder first
   * (`addActionToFolder` calls `removeItemFromAllFolders`; `addItemToChildFolder` does
   * it internally).
   *
   * That leaves the round-trip itself, which is what an unfiled→folder move was always
   * paying. The provider mutates `project.actionFolders` synchronously and PATCHes
   * after, and `project` reaches us by reference, so bumping `moveNonce` paints the
   * final tree now instead of when the network answers.
   */
  moveActionToFolder = (action: IActionDefinitionDto, folderId: string | null) => {
    const folders: IActionFolder[] = this.props.sd.project?.actionFolders || [];
    if (folderId) {
      // Top-level folders and sub-folders share one id space; the drop target is either,
      // so the lookup has to walk children too. (The provider's own getFolderById is
      // typed to screen folders; the helper under it is the generic one.)
      const target: IActionFolder | undefined = FolderHelper.getFolderById<IActionFolder>(folderId, folders);
      if (!target) return;
      if (target.parentFolderId) {
        this.props.sd.addActionToFolderInFolder(target.parentFolderId, target.id, action.id);
      } else {
        this.props.sd.addActionToFolder(target.id, action.id);
      }
    } else {
      removeActionFromItsFolder(this.props.sd, action.id);
    }
    this.setState((prev: IAiHubState) => ({ moveNonce: prev.moveNonce + 1 }));
  };

  /** Rows sharing a droppable: a folder's rows carry its id, unfiled rows share one list. */
  getRowGroupId = (row: AiHubRow): string => {
    if (row.kind === "folder") return row.folder.id;
    return row.folderId ?? UNFILED_GROUP;
  };

  handleRowDragEnd = (result: DropResult) => {
    this.clearFolderHover();
    const { source, destination, draggableId, reason } = result;
    // Escape aborts a drag with no destination too. Only a real drop may move anything,
    // or cancelling would file the row somewhere the user just backed out of.
    if (reason === "CANCEL") return;

    const action: IActionDefinitionDto | undefined = this.getAIItems().find(
      (item: IActionDefinitionDto) => item.id === draggableId
    );
    if (!action) return;

    // Dropped clear of every group. The unfiled group only exists while something IS
    // unfiled, so with everything filed there is no target to aim at — which made
    // "take this out of its folder" undraggable exactly when it was the only move
    // left. Letting go outside is that gesture.
    if (!destination) {
      if (source.droppableId === UNFILED_GROUP) return;
      this.moveActionToFolder(action, null);
      return;
    }

    if (destination.droppableId === source.droppableId) return;
    this.moveActionToFolder(action, destination.droppableId === UNFILED_GROUP ? null : destination.droppableId);
  };

  // Spring-loaded expand: a collapsed folder opens only after the drag dwells over
  // it, so merely passing across folders does not fire a re-render each time.
  private folderHoverTimer: ReturnType<typeof setTimeout> | null = null;
  private folderHoverId: string | null = null;

  clearFolderHover = () => {
    if (this.folderHoverTimer) clearTimeout(this.folderHoverTimer);
    this.folderHoverTimer = null;
    this.folderHoverId = null;
  };

  handleRowDragUpdate = (update: DragUpdate) => {
    const destination: string | undefined = update?.destination?.droppableId;
    if (!destination || destination === UNFILED_GROUP || destination === this.folderHoverId) {
      if (!destination || destination === UNFILED_GROUP) this.clearFolderHover();
      return;
    }
    if (this.isFolderOpen(destination)) return;
    this.clearFolderHover();
    this.folderHoverId = destination;
    this.folderHoverTimer = setTimeout(() => {
      this.setState((prev: IAiHubState) => ({
        collapsedFolderIds: prev.collapsedFolderIds.filter((id: string) => id !== destination)
      }));
      this.folderHoverTimer = null;
    }, 350);
  };

  componentWillUnmount() {
    this.isMounted_ = false;
    this.updateModelStatus.dispose();
    this.clearFolderHover();
  }

  renderFolderModals = () => {
    const { folderModal } = this.state;
    return (
      <>
        <FolderCreateComponent
          showModal={folderModal.isOpen && !folderModal.editing}
          closeModal={this.closeFolderModal}
          dataSourceType={this.getFolderDatasourceId()}
          folderParentFolderId={folderModal.parentFolderId}
        />
        <FolderCreateComponent
          showEditModal={folderModal.isOpen && !!folderModal.editing}
          closeEditModal={this.closeFolderModal}
          editingFolderId={folderModal.editing?.id}
          editingFolderName={folderModal.editing?.name}
          dataSourceType={this.getFolderDatasourceId()}
          folderParentFolderId={folderModal.editing?.parentFolderId}
        />
        <DeleteModal
          itemName={this.state.folderToDelete?.name || ""}
          deleteType="Folder"
          triggerModal={!!this.state.folderToDelete}
          closeModal={() => this.setState({ folderToDelete: null })}
          deleteItem={this.deleteFolder}
        />
      </>
    );
  };

  renderModelUpdateModal = () => {
    const { modelUpdateModal } = this.state;
    const { action } = modelUpdateModal;
    const isShutdown = modelUpdateModal.type === "shutdown";
    const statusLabel = isShutdown ? "Shutdown" : "Deprecated";
    const statusColor = isShutdown ? "red" : "yellow";
    const statusTextClass = isShutdown ? "shutdown" : "deprecated";
    const isAgent = action?.actionType === EDatasourceType.AIAgent;
    const actionTypeName = isAgent ? "AI Agent" : "AI Action";
    const activeModelOptions = this.getActiveModelOptions(action?.actionType ?? EDatasourceType.AIActions);

    const hasAnySelection = isAgent
      ? modelUpdateModal.agentModelUpdates.some((u) => u.selectedModelId)
      : !!modelUpdateModal.selectedModelId;

    return (
      <KModal
        className="ai-hub-model-update-modal"
        showModal={modelUpdateModal.isOpen}
        closeModal={this.closeModelUpdateModal}
        header={
          <div className="ai-hub-model-update-modal__header-inner">
            <div
              className={`ai-hub-model-update-modal__icon-container ai-hub-model-update-modal__icon-container--${statusTextClass}`}
            >
              <KuikaSvg type="common" name="alert-triangle" width={20} height={20} color={`var(--${statusColor}-6)`} />
            </div>
            <div className="ai-hub-model-update-modal__title">Model is {statusLabel.toLowerCase()}</div>
            <div className="ai-hub-model-update-modal__subtitle">action required</div>
          </div>
        }
        width={520}
        footer={
          <KModalFooter
            onCancel={this.closeModelUpdateModal}
            submitLabel="Update model"
            onSubmit={this.handleUpdateModel}
            disabled={!hasAnySelection}
            status={this.state.updateModelStatus}
            statusLabels={{ loading: "Updating", success: "Updated" }}
          />
        }
      >
        <div className="ai-hub-model-update-modal__body">
          {isAgent ? (
            <>
              <div className="ai-hub-model-update-modal__description">
                <strong>{action?.name}</strong> uses models that have been{" "}
                <strong className={`ai-hub-model-update-modal__status-text--${statusColor}`}>
                  {statusLabel.toLowerCase()}
                </strong>
                {isShutdown
                  ? ". This AI Agent cannot run until all shutdown models are replaced."
                  : ". Update before the deadline to avoid interruption."}
              </div>
              <div className="ai-hub-model-update-modal__agent-container">
                {modelUpdateModal.agentModelUpdates.map((update, i) => {
                  const agentStatusColor = update.currentStatus === EAiModelStatus.Shutdown ? "red" : "yellow";
                  const agentStatusLabel = update.currentStatus === EAiModelStatus.Shutdown ? "Shutdown" : "Deprecated";
                  return (
                    <div key={i} className="ai-hub-model-update-modal__agent-row">
                      <div className="ai-hub-model-update-modal__agent-label">{update.agentName}</div>
                      <div className="ai-hub-model-update-modal__field">
                        <label className="ai-hub-model-update-modal__label">Current Model</label>
                        <div
                          className={`ai-hub-model-update-modal__current-model ai-hub-model-update-modal__current-model--${agentStatusColor}`}
                        >
                          <span className="ai-hub-model-update-modal__current-model-name">
                            {update.currentModelName}
                          </span>
                          <KuikaBadge content={agentStatusLabel} color={agentStatusColor as KuikaBadgeColor} />
                        </div>
                      </div>
                      <div className="ai-hub-model-update-modal__field">
                        <label className="ai-hub-model-update-modal__label">Replace with</label>
                        <KuikaSelectbox
                          placeholder="Select a model"
                          options={activeModelOptions}
                          value={update.selectedModelId}
                          onChange={(value) =>
                            this.setState((state) => ({
                              modelUpdateModal: {
                                ...state.modelUpdateModal,
                                agentModelUpdates: state.modelUpdateModal.agentModelUpdates.map((u, idx) =>
                                  idx === i
                                    ? {
                                        ...u,
                                        selectedModelId: value as string
                                      }
                                    : u
                                )
                              }
                            }))
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="ai-hub-model-update-modal__description">
                The model <strong>{modelUpdateModal.currentModelName}</strong> used by this {actionTypeName} has been{" "}
                <strong className={`ai-hub-model-update-modal__status-text--${statusColor}`}>
                  {statusLabel.toLowerCase()}
                </strong>{" "}
                {isShutdown
                  ? "and is no longer available. This module cannot run until you assign a new model."
                  : ". This model will be retired soon. Update before the deadline to avoid interruption."}
              </div>

              <div className="ai-hub-model-update-modal__field">
                <label className="ai-hub-model-update-modal__label">Current Model</label>
                <div
                  className={`ai-hub-model-update-modal__current-model ai-hub-model-update-modal__current-model--${statusColor}`}
                >
                  <span className="ai-hub-model-update-modal__current-model-name">
                    {modelUpdateModal.currentModelName}
                  </span>
                  <KuikaBadge content={statusLabel} color={statusColor as KuikaBadgeColor} />
                </div>
              </div>

              <div className="ai-hub-model-update-modal__field">
                <label className="ai-hub-model-update-modal__label">Replace with</label>
                <KuikaSelectbox
                  placeholder="Select a model"
                  options={activeModelOptions}
                  value={modelUpdateModal.selectedModelId}
                  onChange={(value) =>
                    this.setState((state) => ({
                      modelUpdateModal: {
                        ...state.modelUpdateModal,
                        selectedModelId: value as string
                      }
                    }))
                  }
                />
              </div>
            </>
          )}
        </div>
      </KModal>
    );
  };

  render() {
    const activeTab: string = this.getActiveTab();
    const isRecentTab: boolean = activeTab === RECENTLY_MODIFIED_TAB;
    const aiItems: IActionDefinitionDto[] = isRecentTab ? this.getRecentAIItems() : this.getFilteredAIItems();
    const createOptions = this.getCreateOptions();
    const hasFilters: boolean = !!this.state.searchTerm || this.state.selectedTypeFilters.length > 0;
    const hasTypeTabs: boolean = this.getAccessibleTypeTabs().length > 1;
    const rows: AiHubRow[] = this.buildRows(aiItems);
    const hasFolders: boolean = rows.some((row: AiHubRow) => row.kind === "folder");
    // The page parks on the recent tab while the feed is in flight. Without this it would paint
    // an empty table first and claim nothing had changed recently — something it cannot yet know.
    const isFeedPending: boolean = isRecentTab && this.state.feed.status === "loading";

    return (
      <>
        <DeleteModal
          itemName={this.state.actionToDelete?.name || ""}
          triggerModal={this.state.isDeleteActionModalVisible}
          deleteItem={() => this.deleteAIAction()}
          deleteType={"action"}
          closeModal={() =>
            this.setState({
              isDeleteActionModalVisible: false,
              actionToDelete: null
            })
          }
        />
        <KuikaListPage className="ai-hub" data-testid="aiHubScreen">
          {/* Titled after the page's job — managing the functions you already have —
              not after the create cards, which are one element within it. */}
          <KuikaListPageHeader
            icon={<KuikaSvg name="ai-hub" type="topbar-icons" width={20} height={20} />}
            title="AI Functions"
            titleTestId="aiHubTitle"
          />

          <KuikaCreateCardRow>
            {createOptions.map((option) => (
              <KuikaCreateCard
                key={option.type}
                title={option.heading}
                description={option.description}
                icon={<KuikaSvg type="datasource-icons" name={option.iconName} width={24} height={24} />}
                iconBackground={option.bgColor}
                iconColor={option.iconColor}
                onClick={option.onClick}
                data-testid={`aiHubCreate-${option.iconName}`}
              />
            ))}
          </KuikaCreateCardRow>

          <KuikaListPageToolbar
            tabs={
              <KuikaTabs
                size="lg"
                options={this.getTabOptions()}
                value={activeTab}
                onChange={(value: string) => this.setState({ pickedTab: value })}
                dataTestId="aiHubTabs"
              />
            }
            search={
              <KuikaInput
                size="md"
                placeholder="Search by name..."
                value={this.state.searchTerm}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                  this.handleSearchChange(String(value))
                }
                data-testid="aiHubSearch"
              />
            }
            filters={
              <>
                {!hasTypeTabs && (
                  <KuikaSelectbox
                    className="ai-hub__type-filter"
                    size="md"
                    mode="multiple"
                    placeholder="All types"
                    value={this.state.selectedTypeFilters}
                    onChange={(selectedTypes) => this.handleTypeFilterChange(selectedTypes as EDatasourceType[])}
                    options={this.getTypeOptions()}
                    prefix={<KuikaSvg name="layout-rows" width={12} height={12} />}
                    maxTagCount={1}
                    maxTagPlaceholder={(omittedValues: unknown[]) => {
                      const count = omittedValues?.length || 0;
                      if (!count) return null;
                      return <span className="ai-hub__tag-counter">{count}</span>;
                    }}
                    showTagCrossIcon={false}
                    showOptionCheckboxes
                    data-testid="aiHubTypeFilter"
                  />
                )}
                {/* The recent tab is the feed's order, newest change first. A Sort-by shown there
                    would display a choice the list is not honouring; `state.sort` is kept, so
                    returning to another tab restores it. */}
                {!isRecentTab && (
                  <KuikaSelectbox
                    className="ai-hub__sort-filter"
                    size="md"
                    placeholder="Sort by"
                    allowClear
                    value={this.state.sort}
                    options={SORT_OPTIONS}
                    prefix={<KuikaSvg name="sort-descending" width={12} height={12} />}
                    onChange={(value: string | null) => this.setState({ sort: (value as AiFunctionSortKey) ?? null })}
                    data-testid="aiHubSortFilter"
                  />
                )}
                {this.canCreateFolder() && (
                  <KuikaButton
                    type="secondary"
                    size="md"
                    onClick={this.openNewFolderModal}
                    data-testid="aiHubNewFolder"
                  >
                    <KuikaSvg name="folder-plus" type="sidebar-icons" width={14} height={14} />
                    New folder
                  </KuikaButton>
                )}
              </>
            }
          />

          <AiModelDeprecationBanners
            shutdownItems={this.getShutdownModelsUsage().map((i) => i.name)}
            deprecatedItems={this.getDeprecatedModelsUsage().map((i) => i.name)}
            onShutdownAction={this.handleFixNow}
            onDeprecatedAction={this.handleReviewAll}
          />

          <KuikaListTable<AiHubRow>
            data={rows}
            columns={this.getTableColumns()}
            rowActions={this.getRowActions()}
            rowKey={(row: AiHubRow) => row.id}
            // The table owns the hierarchy: it flattens the tree, indents by depth and
            // asks isRowExpanded what is open. Nothing here builds a row list by hand.
            tree={{
              getSubRows: (row: AiHubRow) => (row.kind === "folder" ? row.children : undefined),
              isRowExpanded: this.isRowExpanded,
              treeColumnId: "name"
            }}
            // Folders and unfiled functions are siblings at the root, so a sort done HERE
            // would interleave them. The headers drive the Sort-by state instead.
            // No header arrow on the recent tab either — it would point at a sort the rows ignore.
            sorting={isRecentTab ? [] : this.getSortingState()}
            onSortingChange={this.handleSortingChange}
            manualSorting
            // Same reason the Sort-by steps aside: the recent tab has an order of its own.
            disableSorting={isRecentTab}
            rowClassName={(row: AiHubRow) => (row.kind === "folder" ? "ai-hub__folder-row" : undefined)}
            // Rows drag between folders; a folder header is a target, not cargo.
            dragDrop={
              this.canManage() && hasFolders
                ? {
                    getGroupId: this.getRowGroupId,
                    getDraggableId: (row: AiHubRow) => row.id,
                    isRowDraggable: (row: AiHubRow) => row.kind === "action",
                    handleLabel: "Drag to move into a folder",
                    onDragEnd: this.handleRowDragEnd,
                    onDragUpdate: this.handleRowDragUpdate
                  }
                : undefined
            }
            // The rows come from context and are ready on mount; only the model
            // catalog, the template list and — on the recent tab — the modification
            // feed are still in flight. Spin only when there is nothing to show yet,
            // so the list is not held back by a fetch it does not depend on — but an
            // empty table does not flash "nothing here" before the first load settles
            // either.
            loading={(this.state.isLoading || isFeedPending) && rows.length === 0}
            revealRowKey={this.state.revealRowKey}
            onRowRevealed={this.handleRowRevealed}
            emptyVariant="page"
            emptyIcon={this.renderEmptyIcon(hasFilters, isRecentTab)}
            emptyTitle={this.getEmptyTitle(hasFilters, isRecentTab)}
            emptyDescription={this.getEmptyDescription(hasFilters, isRecentTab)}
            data-testid="aiHubTable"
          />
        </KuikaListPage>
        {this.renderModelUpdateModal()}
        {this.renderFolderModals()}
      </>
    );
  }

  /**
   * Only offer a per-type tab when more than one type is reachable — otherwise the
   * single type IS "All Functions" and the extra tab is noise.
   */
  getAccessibleTypeTabs = (): { type: EDatasourceType; label: string }[] =>
    TYPE_TABS.filter((t: { type: EDatasourceType; label: string }) => FeatureGates.canAccessAiDatasourceType(t.type));

  getTabOptions = (): IAiHubTabOption[] => {
    const accessibleTypes: { type: EDatasourceType; label: string }[] = this.getAccessibleTypeTabs();
    const typeTabs: IAiHubTabOption[] =
      accessibleTypes.length > 1
        ? accessibleTypes.map((t: { type: EDatasourceType; label: string }): IAiHubTabOption => ({
            value: t.label,
            label: t.label
          }))
        : [];
    return [
      { value: RECENTLY_MODIFIED_TAB, label: RECENTLY_MODIFIED_TAB, dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID },
      { value: ALL_TAB, label: ALL_TAB },
      ...typeTabs
    ];
  };

  /** The glyph over an empty list: what is filtering it, or what the tab is about. */
  renderEmptyIcon = (hasFilters: boolean, isRecentTab: boolean): React.ReactNode => {
    if (hasFilters) return <KuikaSvg name="search-off" width={32} height={32} />;
    if (isRecentTab) return <KuikaSvg name="history" width={32} height={32} />;
    return <KuikaSvg name="ai-hub" type="topbar-icons" width={32} height={32} />;
  };

  getEmptyTitle = (hasFilters: boolean, isRecentTab: boolean): string => {
    if (hasFilters) return "No AI functions match your filters";
    if (isRecentTab) return "Nothing changed recently";
    return "No AI functions yet";
  };

  getEmptyDescription = (hasFilters: boolean, isRecentTab: boolean): string => {
    if (hasFilters) return "Try a different search term or clear the filters.";
    if (isRecentTab) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      return this.state.feed.status === "failed"
        ? "We couldn't load recent activity — open All Functions to see every function."
        : `AI functions edited in the last ${RECENTLY_MODIFIED_WINDOW_DAYS} days appear here.`;
    }
    return "Create an AI Action, AI Agent, or Vision Action to get started.";
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

type AiHubDispatch = (action: DomainAccessModalActionTypes | UnsavedChangesModalModuleActionTypes) => void;

const mapDispatchToProps = (dispatch: AiHubDispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName: string | string[]) =>
      dispatch(showDomainAccessRequiredModal(accessName)),
    setNextCallbackFunction: (cb: () => void) => dispatch(setNextCallbackFunction(cb))
  };
};

const aiHub = connect(null, mapDispatchToProps)(withSD<IAiHubProps>(AiHub));
export { aiHub as AiHub };
